'use client'

import { useEffect, useRef } from 'react'
import { useParams, usePathname } from 'next/navigation'
import { datadogLogs } from '@datadog/browser-logs'

import {
  addEventProperties,
  identify,
  loadHeap,
  resetIdentity,
  track,
} from './heap'
import { resolveSkipReason, type SkipReason } from './skip-reason'
import { templatePath } from './template-path'
import type { UsageAnalyticsProps } from './types'

function logInit(outcome: 'loaded' | 'failed'): void
function logInit(outcome: 'skipped', reason: SkipReason): void
function logInit(outcome: string, reason?: SkipReason): void {
  try {
    datadogLogs.logger.info('heapInit', { outcome, reason })
  } catch {
    // Telemetry failure must not surface to the user.
  }
}

export function UsageAnalytics({
  appId,
  userId,
  portalType,
  appType,
  userRole,
}: UsageAnalyticsProps) {
  const loaded = useRef(false)
  const path = templatePath(usePathname(), useParams())

  useEffect(() => {
    const skipReason = resolveSkipReason(window.location.hostname, navigator)
    if (skipReason) {
      logInit('skipped', skipReason)
      return
    }
    loadHeap(appId).then(
      () => logInit('loaded'),
      () => {
        loaded.current = false
        logInit('failed')
      },
    )
    identify(userId)
    addEventProperties({ portalType, appType, userRole })
    loaded.current = true
    // A shared workstation must not carry one user's identity to the next.
    return () => {
      loaded.current = false
      resetIdentity()
    }
  }, [appId, userId, portalType, appType, userRole])

  // Keep after the init effect; it reads the ref that effect sets.
  useEffect(() => {
    if (loaded.current) track('pageViewed', { path })
  }, [path, userId])

  return null
}
